import { format } from "date-fns";

// Days of the week and months.
const dateTerms = new Set([
  "sun",
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",

  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",

  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",

  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
]);

// Function to determine if a search term is related to a date. Date formatting is costly.
// It is cheaper to check if we need to search by date first.
const isSearchTermDateRelated = (searchTerm) => {
  // Normalize the search term to remove any punctuation, then split it into parts
  const terms = searchTerm
    .toLowerCase()
    .replace(/[^0-9a-z]+/gi, " ") // Replace non-alphanumeric characters with spaces
    .trim()
    .split(/\s+/); // Split the string by spaces to get all parts

  // Iterate through each word of the search term
  for (const term of terms) {
    // Check each word against known date-related terms
    if (dateTerms.has(term)) {
      return true; // A date-related term is found
    }
    // If the term is numeric and has a length of 4, 2, or 1, it could be a year, month, or day
    if (
      !isNaN(term) && // The term is a number
      (term.length === 4 || term.length === 2 || term.length === 1) // The term is of a valid date-related length
    ) {
      return true; // The term is likely part of a date
    }
  }
  // If no date-related terms are found, return false
  return false;
};

const formatDateVariants = (date) => {
  const formats = [
    "yyyy MM dd", // Year, month, and day with leading zeros
    "yyyy M d", // Year, month without leading zero, and day without leading zero
    "yyyy M dd", // Year, month without leading zero, and day with leading zero
    "yyyy MM d", // Year, month with leading zero, and day without leading zero
    "yyyy MM", // Year and month with leading zero
    "yyyy M", // Year and month without leading zero
    "yyyy", // Just the year
    "MMMM yyyy", // Full month name and year
    "MMM yyyy", // Abbreviated month name and year
    "MMMM dd", // Full month name and day with leading zero
    "MMMM d", // Full month name and day without leading zero
    "MMM dd", // Abbreviated month name and day with leading zero
    "MMM d", // Abbreviated month name and day without leading zero
    "MM dd", // Numeric month and day with leading zeros
    "M dd", // Numeric month without leading zero and day with leading zero
    "MM d", // Numeric month with leading zero and day without leading zero
    "M d", // Numeric month and day without leading zeros

    "EEEE", // Full days of the week
    "EEE", // Abbreviated days of the week
  ];

  // Generate date strings for each format
  let dateVariants = formats.map((fmt) => format(date, fmt).toLowerCase());

  // Handle different separators: space, hyphen, and slash
  const separators = [" ", "-", "/"];
  let separatedDateVariants = [];

  separators.forEach((separator) => {
    separatedDateVariants = separatedDateVariants.concat(
      dateVariants.map((variant) => variant.replace(/ /g, separator))
    );
  });

  // Remove duplicates and return the unique date variants
  return [...new Set([...dateVariants, ...separatedDateVariants])];
};

// Function to normalize date strings by removing non-alphanumeric characters (like commas)
// and converting to lowercase for a more flexible comparison.
const normalizeDateString = (dateString) => {
  return dateString.replace(/[^0-9a-zA-Z]+/g, " ");
};

// Function to perform a normalized search on date fields.
const matchesDate = (date, searchTerm) => {
  const normalizedSearchTerm = normalizeDateString(searchTerm);

  // Generate an array of different date format strings
  const dateVariants = formatDateVariants(date);

  // If the search term follows a 'yyyy-MM' format, perform a stricter match
  if (/^\d{4}-\d{2}$/.test(searchTerm)) {
    // Extract year and month from the search term for comparison
    const [searchYear, searchMonth] = searchTerm.split("-");

    // Extract year and month from the date for comparison
    const dateYear = format(date, "yyyy");
    const dateMonth = format(date, "MM");

    // Perform a direct comparison
    return searchYear === dateYear && searchMonth === dateMonth;
  }

  // Check if the search term is present in any of the date variants
  return dateVariants.some((dateVariant) => {
    // Split the date variant and search term into parts to allow partial matching
    const dateParts = dateVariant.split(" ");
    const searchTermParts = normalizedSearchTerm.split(" ");

    // Check if every part of the search term is found in the date parts
    return searchTermParts.every((part) => dateParts.includes(part));
  });
};

const homeRowFilter = (row, loweredSearchTerm, selectedTags) => {
  try {
    // console.log("row:", row);

    const dateRelatedSearch = isSearchTermDateRelated(loweredSearchTerm);

    return (
      (loweredSearchTerm === "" ||
        // Entry UUID
        row.original_entry_id.toLowerCase().includes(loweredSearchTerm) ||
        // Entry Type
        row.entry_type.toLowerCase().includes(loweredSearchTerm) ||
        // Dates
        (dateRelatedSearch &&
          (matchesDate(new Date(row.appointment_date), loweredSearchTerm) ||
            matchesDate(new Date(row.date_modified), loweredSearchTerm))) ||
        // Patient Unique ID
        row.unique_id.toLowerCase().includes(loweredSearchTerm) ||
        // Patient Full Name
        row.patient_owner_full_name.toLowerCase().includes(loweredSearchTerm) ||
        // Entry Tags
        row.classification_tags.toString().includes(loweredSearchTerm) ||
        // Entry Summary
        row.summary.toLowerCase().includes(loweredSearchTerm)) &&
      // Selected Tags
      (selectedTags.size === 0 ||
        row.classification_tags.some((row_tag) => selectedTags.has(row_tag)))
    );
  } catch (error) {
    console.error("Error caught:", error);
    console.log(row);
    return false;
  }
};

const entryRowFilter = (row, loweredSearchTerm, selectedTags) => {
  try {
    // console.log("row:", row);

    const dateRelatedSearch = isSearchTermDateRelated(loweredSearchTerm);

    return (
      (loweredSearchTerm === "" ||
        // Entry UUID. Handle All Entry, and Regular Entries
        (row.original_entry_id ? row.original_entry_id : row.id)
          .toLowerCase()
          .includes(loweredSearchTerm) ||
        // Entry Type
        row.entry_type.toLowerCase().includes(loweredSearchTerm) ||
        // Dates
        (dateRelatedSearch &&
          (matchesDate(new Date(row.appointment_date), loweredSearchTerm) ||
            matchesDate(new Date(row.date_modified), loweredSearchTerm))) ||
        // Entry Tags
        row.classification_tags.toString().includes(loweredSearchTerm) ||
        // Entry Summary
        row.summary.toLowerCase().includes(loweredSearchTerm)) &&
      // Selected Tags
      (selectedTags.size === 0 ||
        row.classification_tags.some((row_tag) => selectedTags.has(row_tag)))
    );
  } catch (error) {
    console.error("Error caught:", error);
    console.log(row);
    return false;
  }
};

const patientRowFilter = (row, loweredSearchTerm, selectedTags) => {
  try {
    // console.log("row:", row);

    const dateRelatedSearch = isSearchTermDateRelated(loweredSearchTerm);

    return (
      (loweredSearchTerm === "" ||
        // Dates
        (dateRelatedSearch &&
          (matchesDate(
            new Date(row.last_appointment_date),
            loweredSearchTerm
          ) ||
            matchesDate(new Date(row.date_modified), loweredSearchTerm))) ||
        // Patient UUID
        row.id.toLowerCase().includes(loweredSearchTerm) ||
        // Patient Unique ID
        row.unique_id.toLowerCase().includes(loweredSearchTerm) ||
        // Patient Full Name
        row.full_name.toLowerCase().includes(loweredSearchTerm) ||
        row.classification_tags.toString().includes(loweredSearchTerm)) &&
      (selectedTags.size === 0 ||
        row.classification_tags.some((row_tag) => selectedTags.has(row_tag)))
    );
  } catch (error) {
    console.error("Error caught:", error);
    console.log(row);
    return false;
  }
};

const entryThumbnailRowFilter = (row, loweredSearchTerm, selectedTags) => {
  try {
    // console.log("row:", row);

    const dateRelatedSearch = isSearchTermDateRelated(loweredSearchTerm);

    return (
      (loweredSearchTerm === "" ||
        // Entry UUID
        row.original_entry_id.toLowerCase().includes(loweredSearchTerm) ||
        // Entry Type
        row.entry_type.toLowerCase().includes(loweredSearchTerm) ||
        // Dates
        (dateRelatedSearch &&
          (matchesDate(new Date(row.appointment_date), loweredSearchTerm) ||
            matchesDate(new Date(row.date_modified), loweredSearchTerm))) ||
        // Entry Tags
        row.classification_tags.toString().includes(loweredSearchTerm) ||
        // Entry Summary
        row.summary.toLowerCase().includes(loweredSearchTerm)) &&
      // Selected Tags
      (selectedTags.size === 0 ||
        row.classification_tags.some((row_tag) => selectedTags.has(row_tag)))
    );
  } catch (error) {
    console.error("Error caught:", error);
    console.log(row);
    return false;
  }
};

export {
  homeRowFilter,
  entryRowFilter,
  patientRowFilter,
  entryThumbnailRowFilter,
};
